import styled from "styled-components";
import { useParams, useLocation } from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useMasking} from "../../common/hook";
import moment from "moment";
import DatepickerRange from "../molecules/DatepickerRange";
import { useMutation, useQuery } from "@tanstack/react-query";
import Loading from "./Loading";
import {useState} from "react";
import SelectBox from "../molecules/SelectBox";
import DataTable from "../organisms/DataTable";
import axios from "axios";
import { Button, Col, Form, Row, Modal } from "react-bootstrap";
import TableFilterBox from "../organisms/TableFilterBox";
import TableHeader from "../organisms/TableHeader";
import TableFooter from "../organisms/TableFooter";

import TablePagination from "../molecules/TablePagination";

const TableWrap = styled.div`
    flex: 1 1;
    display: flex;
    flex-direction: column;

    .data-table {
        flex-grow: 1;
    }

    .revisit-badge {
        background: #007bff;
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: bold;
        animation: pulse 1.5s infinite;
    }

    .cancel-badge {
        background: #dc3545;
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: bold;
    }

    .premium-badge {
        background: #28a745;
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: bold;
    }

    .self-generated-badge {
        background: #8e44ad;
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: bold;
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.05);
        }
        100% {
            transform: scale(1);
        }
    }
`;

const Settlement = () => {
  const location = useLocation();
  const {companyId, branchId} = useParams();

  const perPageOptions = useSelector(state => state.perPageOptions);

  //default Date는 오늘
  const defaultDate = moment().format("YYYY-MM-DD");
  const defaultFilter = {
    startDate: defaultDate,
    endDate: defaultDate,
    branchId: !branchId || branchId === "0" ? "0" : branchId,
    companyId: companyId ? companyId : null,
    page: 1,
    perPage: perPageOptions[2].value,
    orderField: "register_time",
    order: "desc",
    is_premium: null,
    is_report_ready: null
  }
  const dispatch = useDispatch();
  const {masking} = useMasking();

  // (S) state
  const [filter, setFilter] = useState(defaultFilter);
  const [loading, setLoading] = useState(false);
  const [settlementData, setSettlementData] = useState({
    orderField: ["id", "branch_name", "register_time", "name", "birth", "gender", "is_premium", "memo", "is_report_ready", "is_from_worklist"],
    head: [
      "정산ID", "지점", "리포트 등록시간", "이름", "생년월일", "성별", "유료 / 무료 구분", "메모" , "레포트 생성 여부", "고객정보출처", "수정하기"
    ],
    body: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [memo, setMemo] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  
  const handleUpdateSettlement = (item) => {
    console.log(item);
    setSelectedItem(item);
    setMemo(item.memo);
    setIsPremium(item.is_premium);
    setShowModal(true);
  }

  const handleSave = async () => {
    try {
      await axios.post(`/settlement/update/${selectedItem.id}`, {
        is_premium: isPremium,
        memo: memo,
      });
      setShowModal(false);
      refetch(); // 데이터 갱신
    } catch (error) {
      console.error('정산 업데이트 중 오류 발생:', error);
      alert('정산 업데이트 중 오류 발생');
      // TODO: 오류 처리 로직 추가
    }
  }

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['settlement', filter],
    queryFn: async () => {
      try {
        const response = await axios.post('/settlement/list', {
          branchId: filter.branchId,
          companyId: filter.companyId,
          startDate: filter.startDate,
          endDate: filter.endDate,
          page: filter.page,
          perPage: filter.perPage,
          orderField: filter.orderField,
          order: filter.order,
          is_premium: filter.is_premium,
          is_report_ready: filter.is_report_ready
        });
        const formattedData = response.data.data.list.map(item => [
          item.id || '-',
          item.branch_name || '-',
          item.report_register_time || '-',
          masking(item.name || ''),
          masking(item.birth || ''),
        
          item.gender === 1 ? "남자" : "여자", 
          item.is_premium ? <span className="premium-badge">유료</span> : "무료",
          item.memo || '-',
          item.is_report_ready ? "있음" : <span className="cancel-badge">없음</span>,
          item.is_from_worklist ? "워크리스트" : <span className="self-generated-badge">자체생성</span>,
          <Button variant="primary" onClick={() => handleUpdateSettlement(item)}>수정하기</Button>

        ]);
        const total = response.data.data.total;
        
        setSettlementData(prev => ({
          ...prev,
          body: formattedData,
          total
        }));
   
        return response.data;

      } catch (error) {
        throw new Error(error.response?.data?.msg?.ko || '데이터를 가져오는데 실패했습니다');
      }
    },
    onSuccess: (data) => {
      if (!Array.isArray(data.data)) {
        console.error('데이터 형식이 올바르지 않습니다');
        return;
      }
    },
    onError: (error) => {
      console.error('작업 목록 조회 중 오류 발생:', error);
      setSettlementData(prev => ({
        ...prev,
        body: []
      }));
    },
    refetchInterval: 60000 // 1분(60000ms)마다 자동으로 refetch
  });

  const branchOptions = [
    { value: "0", label: "전체" },
    { value: "54", label: "서울서부" },
    { value: "55", label: "서울동부" },
    { value: "56", label: "강남" },  // 강남 branch added for 피부측정
    { value: "57", label: "부산동부" },
    { value: "58", label: "부산서부" },
    { value: "59", label: "대구" },
    { value: "60", label: "인천" },
    { value: "61", label: "울산" },
    { value: "62", label: "경기" },
    { value: "63", label: "강원" },
    { value: "67", label: "전남" },
    { value: "68", label: "경북" },
    { value: "69", label: "경남" }
  ];


  if (isLoading) return <Loading />;

  if (isError) {
    if (error.response) {
      return <div>{error.response.data.msg.ko}</div>
    }
    return <div>{error.message}</div>
  }

  return (
    <TableWrap>
      <TableFilterBox
        handleClickReset={() => {
          setFilter(defaultFilter);
        }}
        handleClickSubmit={() => {
          refetch();
        }}
      >
        <Row>
          <Col sm={6}>
            {(!companyId && !branchId) && (
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3}>
                  지점
                </Form.Label>
                <Col sm={9}>
                  <SelectBox 
                    data={branchOptions}
                    value={filter.branchId}
                    onChange={(selected) => {
                      setFilter(prev => ({
                        ...prev,
                        branchId: selected.value
                      }));
                    }}
                  />
                </Col>
              </Form.Group>
            )}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                측정일
              </Form.Label>
              <Col sm={9}>
                <DatepickerRange 
                  startDate={filter.startDate}
                  endDate={filter.endDate}
                  onChange={(dates) => {
                    setFilter(prev => ({
                      ...prev,
                      startDate: dates[0],
                      endDate: dates[1]
                    }));
                  }}
                />
              </Col>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                유료 고객
              </Form.Label>
              <Col sm={9}>
                <Form.Select
                  value={filter.is_premium === null ? 'all' : filter.is_premium ? 'premium' : 'free'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilter(prev => ({
                      ...prev,
                      is_premium: value === 'all' ? null : value === 'premium'
                    }));
                  }}
                >
                  <option value="all">전체</option>
                  <option value="premium">유료</option> 
                  <option value="free">무료</option>
                </Form.Select>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                레포트 유/무
              </Form.Label>
              <Col sm={9}>
                <Form.Select
                  value={filter.is_report_ready === null ? 'all' : filter.is_report_ready ? 'ready' : 'not_ready'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilter(prev => ({
                      ...prev,
                      is_report_ready: value === 'all' ? null : value === 'ready'
                    }));
                  }}
                >
                  <option value="all">전체</option>
                  <option value="ready">유</option>
                  <option value="not_ready">무</option>
                </Form.Select>
              </Col>
            </Form.Group>
          </Col>
        </Row>
      </TableFilterBox>

      <TableWrap className="mt-4">
        {settlementData?.total > 0 && <TableHeader total={settlementData.total} >
          <div style={{width: "200px"}}>
            <SelectBox 
              onChange={(value) => {
                setFilter({
                  ...filter,
                  page: 1,
                  perPage: value.value
                })
              }} 
              className="w-auto"
              defaultValue={filter.perPage}
              data={perPageOptions}
            />
          </div>
        </TableHeader>}
        <DataTable 
          isError={isError}
          isLoading={isLoading}
          data={settlementData}
          sort={[1,2]} activeSortIdx={2}  



          handleClickSort={(idx, type) => {
            setFilter({
              ...filter,
              orderField: settlementData.orderField[idx],
              order: type,
            });
            
          }}
         
        />
      </TableWrap>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>정산 수정</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formName">
              <Form.Label>이름</Form.Label>
              <Form.Control 
                type="text" 
                value={selectedItem?.name || ''} 
                readOnly
                disabled
                style={{ backgroundColor: 'gray' }}
              />
            </Form.Group>
            <Form.Group controlId="formBirth" className="mt-3">
              <Form.Label>생년월일</Form.Label>
              <Form.Control 
                type="text" 
                value={selectedItem?.birth || ''} 
                readOnly
                disabled
                style={{ backgroundColor: 'gray' }}
              />
            </Form.Group>
            <Form.Group controlId="formGender" className="mt-3">
              <Form.Label>성별</Form.Label>
              <Form.Control 
                type="text" 
                value={selectedItem?.gender === 1 ? "남자" : "여자"} 
                readOnly
                disabled
                style={{ backgroundColor: 'gray' }}
              />
            </Form.Group>
            <Form.Group controlId="formMemo" className="mt-3">
              <Form.Label>메모</Form.Label>
              <Form.Control 
                type="text" 
                value={memo} 
                onChange={(e) => setMemo(e.target.value)} 
              />
            </Form.Group>
            <Form.Group controlId="formIsPremium" className="mt-3">
              <Form.Check 
                type="checkbox" 
                label="유료" 
                checked={isPremium} 
                onChange={(e) => setIsPremium(e.target.checked)} 
              />
            </Form.Group>

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            닫기
          </Button>
          <Button variant="primary" onClick={handleSave}>
            저장
          </Button>
        </Modal.Footer>
      </Modal>
      <TableFooter>
      <TablePagination
        total={settlementData.total} 
        perPage={filter.perPage} 
        currentPage={filter.page}
        onChange={(value) => {
          setFilter({
            ...filter,
            page: value
          })
        }}
      />
      </TableFooter>
    </TableWrap>
  );
}

export default Settlement;